import * as React from 'react';
import { cn } from '~/lib/utils';

interface SideNavButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  label: string;
  isSelected?: boolean;
}

/**
 * SideNavButton - Navigation button for the sidebar
 *
 * States:
 * - Unselected + Idle: transparent background, subtle shadow
 * - Unselected + Hover: light gray background (#F3F4F6)
 * - Unselected + Focus: transparent background, 3px focus ring
 * - Unselected + Disabled: transparent, muted opacity
 * - Selected + Idle: white background, 1px border, shadow
 * - Selected + Hover: gradient overlay on background
 * - Selected + Focus: white background, focus ring
 */
export const SideNavButton = React.forwardRef<HTMLButtonElement, SideNavButtonProps>(
  ({ icon, label, isSelected = false, className, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          // Base styles - 56x56, flex column, centered
          'group relative flex flex-col items-center justify-center',
          'w-14 h-14 gap-2 rounded-md',
          'text-sm font-medium transition-all duration-200',

          // Focus styles
          'focus:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30',

          // Disabled state
          'disabled:pointer-events-none disabled:opacity-50',

          // Unselected state
          !isSelected && 'bg-transparent text-foreground hover:shadow-md',

          // Selected state - matches MobileNav active styling
          isSelected && [
            'bg-background',
            'border border-border',
            'shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]',
            'text-rose-500',
          ],

          className,
        )}
        {...props}
      >
        {/* Icon container */}
        <span
          className={cn(
            'flex items-center justify-center w-5 h-5',
            'transition-colors duration-200',
            isSelected && 'text-rose-500',
          )}
        >
          {icon}
        </span>

        {/* Label */}
        <span
          className={cn(
            'text-[11px] leading-none font-medium',
            'transition-colors duration-200',
            isSelected && 'text-rose-500',
          )}
        >
          {label}
        </span>
      </button>
    );
  },
);

SideNavButton.displayName = 'SideNavButton';
